import React from "react";
import {
  CardContent,
  Grid,
  TextField,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { PlayArrow as GenerateIcon } from "@mui/icons-material";
import { SectionCard } from "./StyledComponents";

const answerTypeOptions = [
  { value: "true_false", label: "True / False" },
  { value: "single_choice", label: "Single Choice" },
  { value: "multiple_choice", label: "Multiple Choice" },
];

const GenerateForm = ({
  formState,
  lookups,
  loadingGenerate,
  handleFormChange,
  handleGenerate,
}) => {
  return (
    <SectionCard>
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Question Bank</InputLabel>
              <Select
                label="Question Bank"
                value={formState.question_bank_id}
                onChange={(e) =>
                  handleFormChange("question_bank_id", e.target.value)
                }
              >
                {lookups.questionBanks.map((bank) => (
                  <MenuItem key={bank.bank_id} value={bank.bank_id}>
                    {bank.bank_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Number of Questions"
              type="number"
              fullWidth
              value={formState.question_count}
              onChange={(e) =>
                handleFormChange("question_count", e.target.value)
              }
              inputProps={{ min: 1, max: 100 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ color: "#fff", mb: 1 }}>
              Answer Types
            </Typography>
            <FormGroup row sx={{ gap: 0.5 }}>
              {answerTypeOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={formState.answer_types.includes(option.value)}
                      onChange={(e) => {
                        const { checked } = e.target;
                        handleFormChange(
                          "answer_types",
                          checked
                            ? [...formState.answer_types, option.value]
                            : formState.answer_types.filter(
                                (type) => type !== option.value
                              )
                        );
                      }}
                      sx={{ color: "#4CAF50" }}
                    />
                  }
                  label={option.label}
                  sx={{ color: "#ccc" }}
                />
              ))}
            </FormGroup>
            <FormHelperText sx={{ color: "#888", mt: 0.5 }}>
              Select at least one for generation
            </FormHelperText>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#888",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 1,
                mt: 1,
              }}
            >
              Filters (optional)
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#666", display: "block", mb: 1 }}
            >
              Leave empty to include all. Pick specific values to narrow
              generation.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Difficulty Levels</InputLabel>
              <Select
                label="Difficulty Levels"
                multiple
                displayEmpty
                value={formState.difficulty_ids}
                onChange={(e) =>
                  handleFormChange("difficulty_ids", e.target.value)
                }
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography component="span" sx={{ color: "#888" }}>
                      Any
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const diff = lookups.difficulties.find(
                          (d) => d.difficulty_id === value
                        );
                        return (
                          <Chip
                            key={value}
                            label={diff?.level_name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )
                }
              >
                {lookups.difficulties.map((difficulty) => (
                  <MenuItem
                    key={difficulty.difficulty_id}
                    value={difficulty.difficulty_id}
                  >
                    {difficulty.level_name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: "#888" }}>
                Leave empty for all
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Moods</InputLabel>
              <Select
                label="Moods"
                multiple
                displayEmpty
                value={formState.mood_ids}
                onChange={(e) => handleFormChange("mood_ids", e.target.value)}
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography component="span" sx={{ color: "#888" }}>
                      Any
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const mood = lookups.moods.find(
                          (m) => m.mood_type_id === value
                        );
                        return (
                          <Chip
                            key={value}
                            label={mood?.display_name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )
                }
              >
                {lookups.moods.map((mood) => (
                  <MenuItem key={mood.mood_type_id} value={mood.mood_type_id}>
                    {mood.display_name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: "#888" }}>
                Leave empty for all
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Topics</InputLabel>
              <Select
                label="Topics"
                multiple
                displayEmpty
                value={formState.topic_ids}
                onChange={(e) => handleFormChange("topic_ids", e.target.value)}
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography component="span" sx={{ color: "#888" }}>
                      Any
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const topic = lookups.topics.find(
                          (t) => t.topic_type_id === value
                        );
                        return (
                          <Chip
                            key={value}
                            label={topic?.display_name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )
                }
              >
                {lookups.topics.map((topic) => (
                  <MenuItem
                    key={topic.topic_type_id}
                    value={topic.topic_type_id}
                  >
                    {topic.display_name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: "#888" }}>
                Leave empty for all
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!formState.topic_ids.length}>
              <InputLabel>Sub-Topics</InputLabel>
              <Select
                label="Sub-Topics"
                multiple
                displayEmpty
                value={formState.subtopic_ids}
                onChange={(e) => handleFormChange("subtopic_ids", e.target.value)}
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography component="span" sx={{ color: "#888" }}>
                      {formState.topic_ids.length ? "Any" : "Select topics first"}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const subtopic = lookups.subtopics.find(
                          (st) => st.subtopic_type_id === value
                        );
                        return (
                          <Chip
                            key={value}
                            label={subtopic?.display_name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )
                }
              >
                {lookups.subtopics.map((subtopic) => (
                  <MenuItem
                    key={subtopic.subtopic_type_id}
                    value={subtopic.subtopic_type_id}
                  >
                    {subtopic.display_name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: "#888" }}>
                Leave empty for all. Select topics first to enable.
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={
                loadingGenerate ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <GenerateIcon />
                )
              }
              disabled={loadingGenerate}
              onClick={handleGenerate}
            >
              Generate Questions
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </SectionCard>
  );
};

export default GenerateForm;
